import React, { useState, useEffect, useCallback, useRef } from 'react';
import { auth, db } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import {
  doc, getDoc, setDoc, getDocs, collectionGroup, query, where, serverTimestamp, onSnapshot
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import NativeReaderNotice from './components/NativeReaderNotice';
import { Card, CardContent } from './components/ui/card';
import { Button } from './components/ui/button';
import {
  Headphones, BookOpen, ShoppingCart, ArrowLeft, Loader2, Clock
} from 'lucide-react';
import AudiobookPlayer from './AudiobookPlayer';
import EbookReader from './EbookReader';
import SignInForm from './components/SignInForm';
import { isOnline } from './lib/offlineStorage';
import { getRentalDownload } from './lib/offlineRentalStore';
import { resolveFreeAccess } from './lib/freeAccess';

/* ═══════════════════════════════════════════
   PURCHASE GATE
   Checks ownership before showing content.
   Routes: /listen/{audiobookId} and /read/{ebookId}
═══════════════════════════════════════════ */

const functions = getFunctions(undefined, 'europe-west2');

export default function PurchaseGate({ contentType, contentId, contentAuthorId }) {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  // 'confirming' is the interim state shown after a Stripe rental checkout while we
  // wait for the webhook to write the rental doc. Initialise straight into it when we
  // land back from checkout (session_id in the URL) so the Rent card never flashes.
  const [status, setStatus] = useState(() =>
    contentType === 'audiobook' &&
    new URLSearchParams(window.location.search).get('session_id')
      ? 'confirming'
      : 'loading'
  ); // loading | confirming | owned | purchase | error
  const [content, setContent] = useState(null);
  const [authorName, setAuthorName] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState(null);
  // True when an audiobook rental exists but has expired / is no longer active,
  // so we can tell the reader their rental ended (buy options still shown).
  const [rentalEnded, setRentalEnded] = useState(false);
  // Rent-button state — separate from the Buy button so the two never interfere.
  const [rentLoading, setRentLoading] = useState(false);
  const [rentNotice, setRentNotice] = useState('');
  // Inline rent sign-in: showRentSignIn toggles the email-code form on the rent card;
  // pendingRent marks that a rental checkout should fire automatically once sign-in
  // completes and the local auth listener populates `user`.
  const [showRentSignIn, setShowRentSignIn] = useState(false);
  const [pendingRent, setPendingRent] = useState(false);
  // True once the post-checkout confirmation has taken longer than ~15s. Copy change
  // only; the rental listener keeps running underneath.
  const [confirmSlow, setConfirmSlow] = useState(false);
  // Live handles for the post-checkout flow, so both the effect cleanup and a manual
  // re-check can tear them down: the rental onSnapshot unsubscribe and the slow timer.
  const unsubRentalRef = useRef(null);
  const slowTimerRef = useRef(null);

  const isAudiobook = contentType === 'audiobook';
  const purchaseCollection = isAudiobook ? 'audiobookPurchases' : 'ebookPurchases';
  const contentKey = isAudiobook ? 'audiobookId' : 'ebookId';
  const collName = isAudiobook ? 'audiobooks' : 'ebooks';

  // The reader is returning from a Stripe rental checkout when session_id is present.
  // createAudiobookRentalCheckout's success_url appends it; that is all we rely on.
  const returningFromCheckout = isAudiobook &&
    Boolean(new URLSearchParams(window.location.search).get('session_id'));

  // Tear down the post-checkout listener and slow timer. Safe to call repeatedly.
  const stopConfirmWatch = useCallback(() => {
    if (slowTimerRef.current) {
      clearTimeout(slowTimerRef.current);
      slowTimerRef.current = null;
    }
    if (unsubRentalRef.current) {
      unsubRentalRef.current();
      unsubRentalRef.current = null;
    }
  }, []);

  // Wait for Firebase Auth
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthReady(true);
    });
    return unsub;
  }, []);

  // Find the content doc via collection group query.
  // Constrain to status in ['live','hidden'] so the query only requests docs the
  // reader is allowed to read — matching the security rule. An unconstrained
  // collection-group query is rejected wholesale with permission-denied if the
  // project contains any audiobook/ebook the reader can't read (e.g. a draft),
  // which would make checkAccess error out. (Owned content with a non-readable
  // status is fetched directly via fetchContentDirect using the purchase/rental
  // doc's authorId, so this filter does not affect paid users.)
  const findContent = useCallback(async () => {
    const allSnap = await getDocs(
      query(collectionGroup(db, collName), where('status', 'in', ['live', 'hidden']))
    );
    const match = allSnap.docs.find(d => d.id === contentId);
    if (!match) return null;

    const data = { id: match.id, ...match.data() };
    const pathParts = match.ref.path.split('/');
    data.authorId = pathParts[1]; // authors/{authorId}/audiobooks/{id}

    // Fetch author name
    try {
      const authorSnap = await getDoc(doc(db, 'authors', data.authorId));
      if (authorSnap.exists()) {
        const a = authorSnap.data();
        setAuthorName(a.penName || a.name || '');
      }
    } catch { /* skip */ }

    setContent(data);
    return data;
  }, [collName, contentId]);

  // Main access check. On a normal load this is a one-shot read. When the reader is
  // returning from a Stripe rental checkout we also watch the rental doc with a live
  // listener and re-check on every change, so access is granted the moment the webhook
  // writes it. We never auto-fall-back to the Rent card while confirming.
  useEffect(() => {
    if (!authReady) return;

    if (!user) {
      // Signed-out. For an audiobook reached via the two-segment /listen/{authorId}/{id}
      // route we have the authorId, so load metadata with a direct doc read (the per-doc
      // rule allows a signed-out read of a live/hidden audiobook) and show the rent card.
      // No user-specific entitlement checks and no confirming listener here, since both
      // need a signed-in user. Every other signed-out case (ebooks, or an old
      // single-segment audiobook link with no authorId) keeps the redirect to /reader.
      if (!isAudiobook || !contentAuthorId) {
        window.location.href = '/reader';
        return;
      }
      let cancelledGuest = false;
      (async () => {
        // Never sit on the confirming spinner signed-out (the listener cannot run); the
        // rent card is the right destination even if session_id is in the URL.
        setStatus('loading');
        const contentData = await fetchContentDirect(contentAuthorId);
        if (cancelledGuest) return;
        if (!contentData) {
          setError('This content was not found or is no longer available.');
          setStatus('error');
          return;
        }
        setError(null);
        setStatus('purchase');
      })();
      return () => { cancelledGuest = true; };
    }

    let cancelled = false;

    // Start the slow-confirmation copy timer as soon as we're in the confirming flow.
    if (returningFromCheckout && !slowTimerRef.current) {
      slowTimerRef.current = setTimeout(() => {
        if (!cancelled) setConfirmSlow(true);
      }, 15000);
    }

    // Once access is granted: stop watching, and strip session_id so a later refresh
    // doesn't drop the reader back into the confirming state.
    const onGranted = () => {
      stopConfirmWatch();
      if (returningFromCheckout && window.location.search) {
        window.history.replaceState({}, '', window.location.pathname);
      }
    };

    const run = async () => {
      // OFFLINE (native): grant straight from the local download manifest with ZERO
      // Firestore reads. Navigation only — AudiobookPlayer re-verifies the signed
      // entitlement token offline before any decryption.
      if (Capacitor.isNativePlatform()) {
        let online = true;
        try { online = await isOnline(); } catch { online = true; }
        if (!online) {
          const rec = isAudiobook ? await getRentalDownload(user.uid, contentId) : null;
          if (cancelled) return;
          if (rec && rec.book && (!rec.expiresAt || rec.expiresAt > Date.now())) {
            setContent({ id: contentId, authorId: rec.book.authorId, title: rec.book.title, coverUrl: rec.book.coverUrl });
            setAuthorName(rec.book.authorName || '');
            setError(null);
            setStatus('owned');
          } else {
            setError(rec ? 'This rental has ended.' : 'You are offline and this audiobook is not downloaded.');
            setStatus('error');
          }
          return; // never touch Firestore offline
        }
      }
      const granted = await checkAccess();
      if (cancelled) return;
      if (granted) {
        onGranted();
        return;
      }
      // Not granted yet. While returning from checkout, watch the rental doc and
      // re-run the access check on every change until it grants. The listener stays
      // attached until access is granted or the component unmounts.
      if (returningFromCheckout && !unsubRentalRef.current) {
        const rentalRef = doc(db, 'audiobookRentals', `${user.uid}_${contentId}`);
        unsubRentalRef.current = onSnapshot(rentalRef, async () => {
          const ok = await checkAccess();
          if (cancelled) return;
          if (ok) onGranted();
        });
      }
    };

    run();

    return () => {
      cancelled = true;
      stopConfirmWatch();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authReady, user, contentId, contentAuthorId]);

  // Direct doc fetch when we know the authorId (bypasses collection group query)
  const fetchContentDirect = useCallback(async (authorId) => {
    const contentRef = doc(db, 'authors', authorId, collName, contentId);
    const snap = await getDoc(contentRef);
    if (!snap.exists()) return null;

    const data = { id: snap.id, authorId, ...snap.data() };

    try {
      const authorSnap = await getDoc(doc(db, 'authors', authorId));
      if (authorSnap.exists()) {
        const a = authorSnap.data();
        setAuthorName(a.penName || a.name || '');
      }
    } catch { /* skip */ }

    setContent(data);
    return data;
  }, [collName, contentId]);

  async function checkAccess() {
    try {
      // Stay in the confirming state while we re-check after checkout; only show the
      // generic spinner on a normal first load.
      if (!returningFromCheckout) setStatus('loading');
      setRentalEnded(false);

      // 1. Check existing purchase doc
      const purchaseDocId = `${user.uid}_${contentId}`;
      const purchaseSnap = await getDoc(doc(db, purchaseCollection, purchaseDocId));

      if (purchaseSnap.exists()) {
        // Use authorId from purchase doc for direct fetch (avoids collection group permission issues)
        const purchaseData = purchaseSnap.data();
        if (purchaseData.authorId) {
          await fetchContentDirect(purchaseData.authorId);
        } else {
          await findContent();
        }
        setStatus('owned');
        return true;
      }

      // 1b. Audiobooks only: an active, unexpired rental also grants playback.
      // Same client read pattern as the purchase check above. Expiry is also
      // enforced server-side in getAudioUrl, so this is the matching gate.
      if (isAudiobook) {
        const rentalSnap = await getDoc(doc(db, 'audiobookRentals', purchaseDocId));
        if (rentalSnap.exists()) {
          const rentalData = rentalSnap.data();
          const expiresMs = rentalData.expiresAt?.toMillis ? rentalData.expiresAt.toMillis() : 0;
          if (rentalData.status === 'active' && expiresMs > Date.now()) {
            if (rentalData.authorId) {
              await fetchContentDirect(rentalData.authorId);
            } else {
              await findContent();
            }
            setStatus('owned');
            return true;
          }
          // Rental exists but has ended — note it so we can tell the reader,
          // then fall through to load content and show buy options beneath.
          setRentalEnded(true);
        }
      }

      // 2. Load the content to check price and grants
      const contentData = await findContent();
      if (!contentData) {
        setError('This content was not found or is no longer available.');
        setStatus('error');
        return false;
      }

      // 3 & 4. Free access. Either the reader's UID is on the free-access list
      // (a comp — valid even on a PAID title, which is what enables restricted
      // ARCs) or the title is genuinely £0. Grants are keyed by auth UID; see
      // src/lib/freeAccess.js.
      const { hasAccess, grantedFree, claimedFree } = resolveFreeAccess({
        freeAccessReaders: contentData.freeAccessReaders,
        uid: user.uid,
        price: contentData.price,
      });
      if (hasAccess) {
        await setDoc(doc(db, purchaseCollection, purchaseDocId), {
          uid: user.uid,
          [contentKey]: contentId,
          authorId: contentData.authorId,
          title: contentData.title || '',
          authorName: authorName || '',
          price: 0,
          claimedFree,
          grantedFree,
          createdAt: serverTimestamp(),
        });
        setStatus('owned');
        return true;
      }

      // 5. Paid content. If the reader is returning from a rental checkout, the rental
      // doc may not be written yet, so keep them in the confirming state (the listener
      // grants access when it lands) rather than flashing the Rent card.
      setError(null);
      setStatus(returningFromCheckout ? 'confirming' : 'purchase');
      return false;
    } catch (err) {
      console.error('Purchase gate error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      setStatus('error');
      return false;
    }
  }

  // Handle Stripe checkout
  async function handleBuyNow() {
    // Reader-only in native: never start a web checkout from the app.
    if (Capacitor.isNativePlatform()) return;
    if (!content || checkoutLoading) return;
    setCheckoutLoading(true);
    setError(null);

    try {
      const fnName = isAudiobook ? 'createAudiobookCheckout' : 'createEbookCheckout';
      const createCheckout = httpsCallable(functions, fnName);
      const payload = isAudiobook
        ? { audiobookId: contentId, authorId: content.authorId }
        : { ebookId: contentId, authorId: content.authorId };

      const result = await createCheckout(payload);

      if (result.data?.sessionUrl) {
        // On native, open in external browser so Stripe checkout works properly
        // On return, the Firestore purchase doc will already exist
        if (Capacitor.isNativePlatform()) {
          await Browser.open({ url: result.data.sessionUrl });
          setCheckoutLoading(false); // User will return to app after checkout
        } else {
          window.location.href = result.data.sessionUrl;
        }
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError('Something went wrong starting checkout. Please try again.');
      setCheckoutLoading(false);
    }
  }

  // Core six-month rental checkout — mirrors handleBuyNow's Stripe pattern, but calls
  // createAudiobookRentalCheckout. Extracted so it can run both directly (already
  // signed in) and automatically after an inline sign-in completes. Audiobooks only.
  const beginRentalCheckout = useCallback(async () => {
    // Reader-only in native: never start a web checkout from the app.
    if (Capacitor.isNativePlatform()) return;
    setRentLoading(true);
    setError(null);
    setRentNotice('');

    try {
      const createRentalCheckout = httpsCallable(functions, 'createAudiobookRentalCheckout');
      const result = await createRentalCheckout({ audiobookId: contentId, authorId: content.authorId });

      if (result.data?.sessionUrl) {
        // On native, open in external browser so Stripe checkout works properly.
        if (Capacitor.isNativePlatform()) {
          await Browser.open({ url: result.data.sessionUrl });
          setRentLoading(false); // User will return to app after checkout
        } else {
          window.location.href = result.data.sessionUrl;
        }
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Rental checkout error:', err);
      setError('Something went wrong starting your rental. Please try again.');
      setRentLoading(false);
    }
  }, [content, contentId]);

  // Rent click. Signed in: start checkout. Signed out: open the inline sign-in form
  // and mark the rental pending, so checkout fires automatically once signed in,
  // without the reader having to tap Rent again. Buy flow above is untouched.
  function handleRentNow() {
    if (!content || rentLoading) return;

    if (!user) {
      setShowRentSignIn(true);
      setPendingRent(true);
      return;
    }

    beginRentalCheckout();
  }

  // Continuation: once the local auth listener populates `user` after an inline
  // sign-in, hide the form and start the checkout. setPendingRent(false) runs first so
  // this cannot re-fire.
  useEffect(() => {
    if (user && pendingRent) {
      setPendingRent(false);
      setShowRentSignIn(false);
      beginRentalCheckout();
    }
  }, [user, pendingRent, beginRentalCheckout]);

  // "Check again" in the slow-confirmation state: re-run the access check by hand. The
  // live listener is still attached underneath; this just lets an impatient reader
  // force an immediate re-check.
  async function handleCheckAgain() {
    const granted = await checkAccess();
    if (granted) {
      stopConfirmWatch();
      if (window.location.search) {
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }

  // ─── Loading state ───
  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-teal-600" />
          <p className="mt-4 text-sm text-slate-500">Checking access...</p>
        </div>
      </div>
    );
  }

  // ─── Confirming a rental after checkout ───
  // Gold-on-black: the .listening-theme palette resolves the shadcn tokens
  // (background, foreground, primary) to the dark/antique-gold reader values.
  if (status === 'confirming') {
    return (
      <div className="listening-theme flex min-h-screen items-center justify-center bg-background px-6 py-12 text-foreground">
        <div className="w-full max-w-md text-center">
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
          <h2 className="mt-6 text-xl font-semibold text-primary">
            Confirming your rental
          </h2>
          {confirmSlow ? (
            <>
              <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-muted-foreground">
                Your payment has gone through. We are just finalising your rental,
                which is taking a little longer than usual. It will appear here
                automatically. You can refresh if you like, but please do not pay again.
              </p>
              <Button onClick={handleCheckAgain} size="lg" className="mt-6">
                Check again
              </Button>
            </>
          ) : (
            <p className="mt-3 text-sm text-muted-foreground">
              This only takes a moment.
            </p>
          )}
        </div>
      </div>
    );
  }

  // ─── Error state ───
  if (status === 'error') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-md border-slate-200">
          <CardContent className="py-12 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
              {isAudiobook
                ? <Headphones className="h-8 w-8 text-red-400" />
                : <BookOpen className="h-8 w-8 text-red-400" />}
            </div>
            <h2 className="mb-2 text-lg font-semibold text-slate-800">Content unavailable</h2>
            <p className="mb-6 text-sm text-slate-500">{error}</p>
            <Button
              onClick={() => {
                window.location.href = Capacitor.isNativePlatform() ? '/signin-preview' : '/reader';
              }}
              variant="outline"
              className="gap-1.5 border-slate-200"
            >
              <ArrowLeft className="h-4 w-4" /> {Capacitor.isNativePlatform() ? 'Back to my library' : 'Back to portal'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Owned — access granted ───
  if (status === 'owned') {
    // Audiobook: render the full player
    if (isAudiobook && content) {
      return (
        <AudiobookPlayer
          audiobook={{ ...content, authorName }}
          onBack={() => { window.location.href = '/reader'; }}
        />
      );
    }

    // eBook: render the full reader
    if (content) {
      return (
        <EbookReader
          ebook={{ ...content, authorName }}
          onBack={() => { window.location.href = '/reader'; }}
        />
      );
    }
  }

  // ─── Native app is reader-only: never show a purchase/rent card ───
  // All the status === 'owned' branches above already returned the player/reader.
  // If we reach here in native, the reader does not (yet) have access — show the
  // reader-only notice instead of any Buy/Rent CTA. Web is unaffected.
  if (Capacitor.isNativePlatform()) {
    return <NativeReaderNotice />;
  }

  // ─── Purchase card ───
  const price = Number(content?.price || 0);
  // Rent option — audiobooks only, and only when the author has set a rentalPrice.
  const rentalPrice = Number(content?.rentalPrice || 0);
  const canRent = isAudiobook && rentalPrice > 0;

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-md border-slate-200 overflow-hidden">
        {content?.coverUrl && (
          <div className="relative aspect-[3/2] bg-slate-100">
            <img
              src={content.coverUrl}
              alt={content.title || ''}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          </div>
        )}

        <CardContent className="space-y-4 p-6">
          <div>
            <h2 className="text-xl font-bold text-slate-900">
              {content?.title || (isAudiobook ? 'Audiobook' : 'eBook')}
            </h2>
            {authorName && (
              <p className="mt-1 text-sm text-slate-500">by {authorName}</p>
            )}
          </div>

          {rentalEnded && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-center text-sm text-red-700">
              Your rental has ended. Rent again below to keep listening.
            </div>
          )}

          {isAudiobook ? (
            /* Audiobooks are rental-only: no outright purchase, no price display. */
            <p className="text-sm text-slate-600">
              Rent this audiobook to listen for six months.
            </p>
          ) : (
            <>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-center">
                <p className="text-2xl font-bold text-slate-900">
                  {'\u00A3'}{price.toFixed(2)}
                </p>
                <p className="mt-1 text-xs text-slate-500">One-time purchase</p>
              </div>

              <p className="text-sm text-slate-600">
                You need to purchase this eBook to read.
              </p>
            </>
          )}

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Buy is eBooks only; audiobooks are rental-only now. */}
          {!isAudiobook && (
            <Button
              onClick={handleBuyNow}
              disabled={checkoutLoading}
              className="w-full gap-2 bg-slate-900 text-white hover:bg-slate-800"
              size="lg"
            >
              {checkoutLoading ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting to checkout...</>
              ) : (
                <><ShoppingCart className="h-4 w-4" /> Buy now</>
              )}
            </Button>
          )}

          {/* Rent — the leading (and only) option for audiobooks. A signed-out reader
              still sees the Rent button; tapping it opens the inline sign-in below,
              and checkout fires automatically once they are signed in. */}
          {canRent && (
            showRentSignIn ? (
              <div className="space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
                <h3 className="text-center text-sm font-semibold text-slate-900">
                  Sign in to rent
                </h3>
                <SignInForm />
                <button
                  type="button"
                  onClick={() => { setShowRentSignIn(false); setPendingRent(false); }}
                  className="mx-auto block text-sm text-slate-500 underline-offset-4 hover:underline"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <>
                <Button
                  onClick={handleRentNow}
                  disabled={rentLoading}
                  className="w-full gap-2 bg-slate-900 text-white hover:bg-slate-800"
                  size="lg"
                >
                  {rentLoading ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting to checkout...</>
                  ) : (
                    <><Clock className="h-4 w-4" /> Rent for six months, {'£'}{rentalPrice.toFixed(2)}</>
                  )}
                </Button>
                {rentNotice && (
                  <p className="text-center text-sm text-slate-600">{rentNotice}</p>
                )}
              </>
            )
          )}

          <Button
            onClick={() => { window.location.href = '/reader'; }}
            variant="ghost"
            className="w-full gap-1.5 text-slate-500"
          >
            <ArrowLeft className="h-4 w-4" /> Back to browsing
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
